import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { NotifyResellersButton } from "@/components/NotifyResellersButton";
import { toast } from "sonner";
import {
  Package, Images, FileSpreadsheet, ImagePlus, Loader2, Plus, Pencil, Trash2,
  UploadCloud, FileText, Film, FileArchive, Image as ImageIcon, ZoomIn, X, Lock, Globe,
} from "lucide-react";

const CATEGORIES = ["Bike", "Moto", "Accessori"];
const ASSET_CATEGORIES = ["Catalogo", "Video", "Foto", "Social", "Materiale Punto vendita", "Varie"];
const PLATFORMS = ["Instagram Stories", "Facebook Stories", "Instagram Post", "Facebook Post", "Instagram Reel", "Altro"];
const VISIBILITY_OPTIONS = [
  { value: "all", label: "Tutti i rivenditori" },
  { value: "official", label: "Solo Rivenditori Ufficiali" },
];
const CHUNK = 4 * 1024 * 1024; // 4MB
const eur = (n) => {
  const num = Number(n || 0);
  const [i, d] = Math.abs(num).toFixed(2).split(".");
  return `${num < 0 ? "-" : ""}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${d} €`;
};
const emptyProduct = {
  sku: "", model_code: "", name: "", category: "Bike", line: "", description: "", tech_specs: "",
  colors: "", sizes: "", gender: "", ean: "",
  price_excl_vat: "", vat_rate: 22, reseller_discount_pct: 0, stock: 0, size_stock: {},
};

/* -------- Products management -------- */
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importingSpecs, setImportingSpecs] = useState(false);
  const [uploadingImgs, setUploadingImgs] = useState(false);
  const [uploadingProdImgs, setUploadingProdImgs] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingImages, setEditingImages] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const excelRef = useRef(null);
  const specsRef = useRef(null);
  const imgRef = useRef(null);
  const prodImgRef = useRef(null);

  const load = () => api.get("/products").then(({ data }) => setProducts(data.products)).catch(() => {});
  useEffect(() => { load(); }, []);

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import: ${data.created} creati, ${data.updated} aggiornati (${data.total} righe)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import fallito");
    } finally { setImporting(false); if (excelRef.current) excelRef.current.value = ""; }
  };

  const importSpecs = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingSpecs(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/import-specs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Caratteristiche: ${data.updated_products} prodotti aggiornati (${data.rows} righe)`);
      if (data.unmatched_count > 0) toast.warning(`Codici non trovati: ${data.unmatched.join(", ")}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import caratteristiche fallito");
    } finally { setImportingSpecs(false); if (specsRef.current) specsRef.current.value = ""; }
  };

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingImgs(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/products/upload-images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Foto: ${data.matched_count} associate, ${data.unmatched_count} non abbinate`);
      if (data.unmatched_count > 0) toast.warning(`Non abbinate (SKU non trovato): ${data.unmatched.join(", ")}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload foto fallito");
    } finally { setUploadingImgs(false); if (imgRef.current) imgRef.current.value = ""; }
  };

  const openNew = () => { setEditingId(null); setEditingImages([]); setForm(emptyProduct); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditingId(p._id);
    setEditingImages(p.images || []);
    setForm({
      sku: p.sku || "", model_code: p.model_code || "", name: p.name || "", category: p.category || "Bike", line: p.line || "",
      description: p.description || "", tech_specs: p.tech_specs || "",
      colors: (p.colors || []).join(", "), sizes: (p.sizes || []).join(", "),
      gender: p.gender || "", ean: p.ean || "",
      price_excl_vat: p.pricing?.price_excl_vat ?? "", vat_rate: p.pricing?.vat_rate ?? 22,
      reseller_discount_pct: p.pricing?.reseller_discount_pct ?? 0, stock: p.stock ?? 0,
      size_stock: p.size_stock || {},
    });
    setDialogOpen(true);
  };

  const setSizeStock = (size, val) => setForm((f) => ({ ...f, size_stock: { ...(f.size_stock || {}), [size]: val } }));

  const uploadProductImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !editingId) return;
    setUploadingProdImgs(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/products/${editingId}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditingImages(data.images || []);
      toast.success(`${data.added} foto caricate`);
      if ((data.skipped || []).length) toast.warning(`Ignorati (formato non valido): ${data.skipped.join(", ")}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload foto fallito");
    } finally { setUploadingProdImgs(false); if (prodImgRef.current) prodImgRef.current.value = ""; }
  };

  const removeProductImage = async (path) => {
    if (!editingId || !path) return;
    try {
      const { data } = await api.post(`/products/${editingId}/images/delete`, { path });
      setEditingImages(data.images || []);
      toast.success("Foto rimossa");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Rimozione fallita"); }
  };

  const setProductCover = async (path) => {
    if (!editingId || !path) return;
    try {
      const { data } = await api.post(`/products/${editingId}/images/cover`, { path });
      setEditingImages(data.images || []);
      toast.success("Copertina impostata");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Operazione fallita"); }
  };

  const saveProduct = async () => {
    if (!form.sku.trim() || !form.name.trim()) { toast.error("SKU e Nome sono obbligatori"); return; }
    setSaving(true);
    const sizes = form.sizes.split(",").map((s) => s.trim()).filter(Boolean);
    const size_stock = {};
    sizes.forEach((s) => { size_stock[s] = Number(form.size_stock?.[s] ?? form.stock) || 0; });
    const payload = {
      ...form,
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      sizes,
      price_excl_vat: Number(form.price_excl_vat) || 0,
      vat_rate: Number(form.vat_rate) || 22,
      reseller_discount_pct: Number(form.reseller_discount_pct) || 0,
      stock: Number(form.stock) || 0,
      size_stock,
    };
    try {
      if (editingId) await api.patch(`/products/${editingId}`, payload);
      else await api.post("/products", payload);
      toast.success(editingId ? "Prodotto aggiornato" : "Prodotto creato");
      setDialogOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito");
    } finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Eliminare ${p.name} (${p.sku})?`)) return;
    try { await api.delete(`/products/${p._id}`); toast.success("Prodotto eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const clearAll = async () => {
    if (!window.confirm("Svuotare TUTTO il catalogo? Elimina tutti i prodotti (utile prima di caricare il tuo Excel). Azione irreversibile.")) return;
    try { const { data } = await api.delete("/products"); toast.success(`${data.deleted} prodotti eliminati`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const imgSrc = (img) => (img?.storage_path ? fileUrl(img.storage_path) : img?.url);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-brema-card px-4 py-3">
        <p className="text-sm text-muted-foreground">Hai aggiornato i prezzi di listino? Avvisa i Rivenditori Ufficiali della modifica.</p>
        <NotifyResellersButton category="listini" testId="notify-listini-btn" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-brema-card p-5">
          <div className="mb-2 flex items-center gap-2 text-foreground"><FileSpreadsheet className="h-5 w-5 text-brand" /><h3 className="font-semibold">Import Excel prodotti</h3></div>
          <p className="mb-3 text-xs text-muted-foreground">Colonne: SKU (una per taglia), <b>Codice articolo</b>, Nome, Categoria, Linea, Colori, <b>Taglia</b>, Prezzo listino IVA esclusa, IVA, Sconto rivenditore, Stock. Le caratteristiche puoi importarle a parte →</p>
          <input ref={excelRef} type="file" onChange={importExcel} className="hidden" data-testid="excel-input" />
          <Button onClick={() => excelRef.current?.click()} disabled={importing} data-testid="import-excel-btn"
            className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {importing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Import…</> : <><FileSpreadsheet className="mr-2 h-4 w-4" /> Carica Excel</>}
          </Button>
        </div>
        <div className="rounded-xl border border-border bg-brema-card p-5">
          <div className="mb-2 flex items-center gap-2 text-foreground"><FileText className="h-5 w-5 text-brand" /><h3 className="font-semibold">Import caratteristiche</h3></div>
          <p className="mb-3 text-xs text-muted-foreground">Excel a 2 colonne: <b>Codice articolo</b> + <b>Caratteristiche</b> (testi lunghi). Applicate a tutte le taglie; usa un codice base per coprire anche i colori.</p>
          <input ref={specsRef} type="file" onChange={importSpecs} className="hidden" data-testid="specs-input" />
          <Button onClick={() => specsRef.current?.click()} disabled={importingSpecs} data-testid="import-specs-btn"
            className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {importingSpecs ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Import…</> : <><FileText className="mr-2 h-4 w-4" /> Carica caratteristiche</>}
          </Button>
        </div>
        <div className="rounded-xl border border-border bg-brema-card p-5">
          <div className="mb-2 flex items-center gap-2 text-foreground"><ImagePlus className="h-5 w-5 text-brand" /><h3 className="font-semibold">Carica foto (per SKU)</h3></div>
          <p className="mb-3 text-xs text-muted-foreground">Nomina i file con il <b>Codice articolo</b> + numero foto (trattino o underscore): <code className="text-foreground">CODICE-1.jpg</code>, <code className="text-foreground">CODICE-2.jpg</code>. Puoi caricare più foto insieme oppure una <b>cartella ZIP</b>. Aggancio automatico a tutte le taglie.</p>
          <input ref={imgRef} type="file" multiple onChange={uploadImages} className="hidden" data-testid="images-input" />
          <Button onClick={() => imgRef.current?.click()} disabled={uploadingImgs} data-testid="upload-images-btn"
            className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {uploadingImgs ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Upload…</> : <><ImagePlus className="mr-2 h-4 w-4" /> Carica foto / ZIP</>}
          </Button>
        </div>
        <div className="rounded-xl border border-border bg-brema-card p-5">
          <div className="mb-2 flex items-center gap-2 text-foreground"><Plus className="h-5 w-5 text-brand" /><h3 className="font-semibold">Prodotto singolo</h3></div>
          <p className="mb-3 text-xs text-muted-foreground">Aggiungi o modifica manualmente un articolo.</p>
          <Button onClick={openNew} data-testid="new-product-btn"
            className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            <Plus className="mr-2 h-4 w-4" /> Nuovo prodotto
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-brema-card">
        <div className="flex items-center justify-between border-b border-border p-4">
          <h3 className="font-semibold text-foreground">Catalogo ({products.length})</h3>
          {products.length > 0 && (
            <Button size="sm" variant="danger" data-testid="clear-catalog-btn" onClick={clearAll}
              className="rounded-full text-xs">
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Svuota catalogo
            </Button>
          )}
        </div>
        {products.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">Nessun prodotto. Importa l'Excel per iniziare.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {products.map((p) => (
              <div key={p._id} data-testid="admin-product-row" className="flex items-center gap-3 p-3">
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-brema-bg">
                  {(p.images || [])[0] ? (
                    <img src={p.images[0].storage_path ? fileUrl(p.images[0].storage_path) : p.images[0].url} alt="" className="h-full w-full object-cover" />
                  ) : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageIcon className="h-5 w-5" /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{p.name} <span className="font-mono text-xs text-muted-foreground">{p.sku}</span>{p.model_code && <span className="ml-1 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">art. {p.model_code}</span>}</p>
                  <p className="text-xs text-muted-foreground">
                    {p.category}{p.line ? ` · ${p.line}` : ""}{(p.sizes || []).length ? ` · Taglia ${(p.sizes || []).join("/")}` : ""} · Listino {eur(p.pricing?.price_excl_vat)} (IVA escl.)
                    {p.pricing?.reseller_discount_pct > 0 && <span className="text-positive"> · -{p.pricing.reseller_discount_pct}%</span>}
                    {" · "}{(p.images || []).length} foto · stock {p.stock ?? 0}
                  </p>
                </div>
                <Button size="sm" variant="secondary" data-testid="edit-product-btn" onClick={() => openEdit(p)}
                  className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="danger" data-testid="delete-product-btn" onClick={() => del(p)}
                  className="rounded-full text-xs"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto border-border bg-brema-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">{editingId ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle>
            <DialogDescription>Prezzo di listino IVA esclusa; il prezzo rivenditore è calcolato con lo sconto.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label>Foto prodotto {editingImages.length > 0 && <span className="text-muted-foreground">({editingImages.length})</span>}</Label>
                  {editingId && (
                    <>
                      <input ref={prodImgRef} type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={uploadProductImages} className="hidden" data-testid="product-images-input" />
                      <Button type="button" size="sm" variant="secondary" disabled={uploadingProdImgs} onClick={() => prodImgRef.current?.click()} data-testid="product-upload-images-btn"
                        className="h-8 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                        {uploadingProdImgs ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Caricamento…</> : <><ImagePlus className="mr-1.5 h-3.5 w-3.5" /> Carica foto</>}
                      </Button>
                    </>
                  )}
                </div>
                {editingImages.length > 0 ? (
                  <div className="mt-2 grid grid-cols-3 gap-2" data-testid="product-gallery">
                    {editingImages.map((img, i) => (
                      <div key={img.storage_path || img.url || i} data-testid="product-image-card"
                        className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-brema-bg">
                        <button type="button" onClick={() => setLightbox(imgSrc(img))} data-testid="product-image-thumb" className="block h-full w-full">
                          <img src={imgSrc(img)} alt={`foto ${i + 1}`} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-transparent transition-colors group-hover:bg-black/30 group-hover:text-white"><ZoomIn className="h-5 w-5" /></span>
                        </button>
                        {i === 0 && (
                          <span data-testid="product-image-cover-badge" className="absolute left-1 top-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground shadow">Copertina</span>
                        )}
                        {editingId && img.storage_path && (
                          <div className="absolute right-1 top-1 flex gap-1">
                            {i !== 0 && (
                              <button type="button" onClick={() => setProductCover(img.storage_path)} data-testid="product-image-cover-btn"
                                title="Imposta come copertina"
                                className="rounded-full bg-black/60 p-1.5 text-white transition hover:bg-black/80">
                                <ImageIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button type="button" onClick={() => removeProductImage(img.storage_path)} data-testid="product-image-remove-btn"
                              title="Rimuovi foto"
                              className="rounded-full bg-red-600/80 p-1.5 text-white transition hover:bg-red-600">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground">
                    {editingId
                      ? <>Nessuna foto per questo articolo. Usa <b>“Carica foto”</b> qui sopra (PNG/JPG/WebP), oppure la scheda <b>“Carica foto (per SKU)”</b> nominando i file col Codice articolo (es. <code className="text-foreground">{form.model_code || "CODICE"}-1.jpg</code>).</>
                      : <>Salva prima il prodotto, poi riaprilo per caricare le foto. In alternativa usa la scheda <b>“Carica foto (per SKU)”</b> nominando i file col Codice articolo.</>}
                  </p>
                )}
                {editingId && editingImages.length > 0 && (
                  <p className="mt-1.5 text-[11px] text-muted-foreground">Passa sopra una foto per rimuoverla (cestino) o impostarla come copertina. La prima foto è la copertina mostrata ai rivenditori.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Caratteristiche tecniche / Descrizione</Label>
                <Textarea value={form.tech_specs} onChange={set("tech_specs")} data-testid="pf-specs" className="min-h-[240px] resize-y leading-relaxed" />
              </div>
            </div>
            <div className="grid content-start gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>SKU (per taglia) *</Label><Input value={form.sku} onChange={set("sku")} data-testid="pf-sku" /></div>
              <div className="space-y-1.5"><Label>Codice articolo (raggruppa le taglie)</Label><Input value={form.model_code} onChange={set("model_code")} placeholder="Es. GIACCA-GRAVEL-ROSSA" data-testid="pf-model" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Nome *</Label><Input value={form.name} onChange={set("name")} data-testid="pf-name" /></div>
              <div className="space-y-1.5"><Label>Categoria</Label>
                <Select value={form.category} onValueChange={set("category")}>
                  <SelectTrigger data-testid="pf-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Linea / Collezione</Label><Input value={form.line} onChange={set("line")} data-testid="pf-line" /></div>
              <div className="space-y-1.5"><Label>Colori (separati da virgola)</Label><Input value={form.colors} onChange={set("colors")} placeholder="Nero, Giallo" data-testid="pf-colors" /></div>
              <div className="space-y-1.5"><Label>Taglia/e (separati da virgola)</Label><Input value={form.sizes} onChange={set("sizes")} placeholder="S, M, L, XL" data-testid="pf-sizes" /></div>
              <div className="space-y-1.5"><Label>Prezzo listino IVA esclusa (€)</Label><Input type="number" step="0.01" value={form.price_excl_vat} onChange={set("price_excl_vat")} data-testid="pf-price" /></div>
              <div className="space-y-1.5"><Label>IVA (%)</Label><Input type="number" value={form.vat_rate} onChange={set("vat_rate")} data-testid="pf-vat" /></div>
              <div className="space-y-1.5"><Label>Sconto rivenditore (%)</Label><Input type="number" value={form.reseller_discount_pct} onChange={set("reseller_discount_pct")} data-testid="pf-discount" /></div>
              <div className="space-y-1.5"><Label>Genere</Label><Input value={form.gender} onChange={set("gender")} placeholder="uomo / donna / unisex" data-testid="pf-gender" /></div>
              <div className="space-y-1.5"><Label>EAN</Label><Input value={form.ean} onChange={set("ean")} data-testid="pf-ean" /></div>
              {(() => {
                const sizeList = form.sizes.split(",").map((s) => s.trim()).filter(Boolean);
                if (sizeList.length === 0) {
                  return <div className="space-y-1.5 sm:col-span-2"><Label>Stock</Label><Input type="number" value={form.stock} onChange={set("stock")} data-testid="pf-stock" /></div>;
                }
                return (
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>Disponibilità per taglia</Label>
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                      {sizeList.map((s) => (
                        <div key={s} className="space-y-1">
                          <span className="block text-center text-[11px] font-medium text-muted-foreground">{s}</span>
                          <Input type="number" min={0} value={form.size_stock?.[s] ?? form.stock ?? 0}
                            onChange={(e) => setSizeStock(s, e.target.value)} data-testid={`pf-stock-${s}`} className="h-9 text-center" />
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground">Ogni taglia ha la propria giacenza; l'ordine scala solo la taglia scelta.</p>
                  </div>
                );
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveProduct} disabled={saving} data-testid="save-product-btn"
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lightbox && (
        <div data-testid="image-lightbox" onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[110] flex cursor-zoom-out items-center justify-center bg-black/85 p-4 backdrop-blur-sm">
          <img src={lightbox} alt="anteprima" className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain shadow-2xl" />
          <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox(null); }} data-testid="close-lightbox-btn"
            className="absolute right-5 top-5 rounded-full bg-muted p-2 text-foreground transition hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>
      )}
    </div>
  );
}

/* -------- Marketing materials management -------- */
function MaterialsTab() {
  const [assets, setAssets] = useState([]);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Catalogo");
  const [platform, setPlatform] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [visibility, setVisibility] = useState("all");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);
  const [editAsset, setEditAsset] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", category: "Catalogo", platform: "", body_text: "", visibility: "all" });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = () => api.get("/marketing-assets").then(({ data }) => setAssets(data.assets)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!title.trim()) { toast.error("Inserisci un titolo"); return; }
    if (!file && !bodyText.trim()) { toast.error("Aggiungi un file oppure un testo"); return; }
    setUploading(true); setProgress(0);
    try {
      if (file) {
        const { data: initData } = await api.post("/uploads/init");
        const uploadId = initData.upload_id;
        const total = file.size;
        let sent = 0;
        for (let start = 0; start < total; start += CHUNK) {
          const blob = file.slice(start, Math.min(start + CHUNK, total));
          await api.put(`/uploads/${uploadId}/chunk`, blob, { headers: { "Content-Type": "application/octet-stream" } });
          sent += blob.size;
          setProgress(Math.round((sent / total) * 100));
        }
        const { data } = await api.post("/marketing-assets/finalize", {
          upload_id: uploadId, title: title.trim(), category, filename: file.name,
          content_type: file.type || "", platform: category === "Social" ? platform : "", body_text: bodyText.trim(),
          visibility,
        });
        toast.success(`Materiale caricato: ${data.asset.title}`);
      } else {
        const { data } = await api.post("/marketing-assets/text", {
          title: title.trim(), category, platform: category === "Social" ? platform : "", body_text: bodyText.trim(),
          visibility,
        });
        toast.success(`Testo salvato: ${data.asset.title}`);
      }
      setTitle(""); setFile(null); setBodyText(""); setPlatform(""); setVisibility("all"); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload fallito");
    } finally { setUploading(false); setProgress(0); }
  };

  const del = async (a) => {
    if (!window.confirm(`Eliminare "${a.title}"?`)) return;
    try { await api.delete(`/marketing-assets/${a._id}`); toast.success("Materiale eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openEditAsset = (a) => {
    setEditAsset(a);
    setEditForm({
      title: a.title || "", category: a.category || "Catalogo", platform: a.platform || "",
      body_text: a.body_text || "", visibility: a.visibility || "all",
    });
  };

  const saveEditAsset = async () => {
    if (!editAsset) return;
    if (!editForm.title.trim()) { toast.error("Il titolo non può essere vuoto"); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/marketing-assets/${editAsset._id}`, {
        title: editForm.title.trim(), category: editForm.category,
        platform: editForm.category === "Social" ? editForm.platform : "",
        body_text: editForm.body_text, visibility: editForm.visibility,
      });
      toast.success("Materiale aggiornato");
      setEditAsset(null); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito");
    } finally { setSavingEdit(false); }
  };

  const iconFor = (a) => {
    const t = (a.type || "").toUpperCase();
    if (a.is_image) return null;
    if (t === "TESTO") return FileText;
    if (t === "PDF") return FileText;
    if (["MP4", "MOV", "WEBM", "VIDEO"].includes(t)) return Film;
    return FileArchive;
  };
  const isVideoAsset = (a) => ["MP4", "MOV", "WEBM", "VIDEO", "M4V"].includes((a.type || "").toUpperCase());

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-brema-card p-5">
        <div className="mb-4 flex items-center gap-2 text-foreground"><UploadCloud className="h-5 w-5 text-brand" /><h3 className="font-semibold">Carica materiale marketing</h3></div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5 sm:col-span-2"><Label>Titolo</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="asset-title" placeholder="Es. Lookbook Collezione Bici SS26" />
          </div>
          <div className="space-y-1.5"><Label>Categoria</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="asset-category"><SelectValue /></SelectTrigger>
              <SelectContent>{ASSET_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {category === "Social" && (
          <div className="mt-3 space-y-1.5">
            <Label>Formato (Instagram / Facebook)</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger data-testid="asset-platform"><SelectValue placeholder="Seleziona formato" /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="mt-3 space-y-1.5">
          <Label>Testo / Caption (opzionale)</Label>
          <Textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} data-testid="asset-text"
            placeholder="Testo del template social, caption pronta da copiare, note per la pubblicazione…" rows={3} />
        </div>
        <div className="mt-3 space-y-1.5">
          <Label>Visibilità Materiale</Label>
          <Select value={visibility} onValueChange={setVisibility}>
            <SelectTrigger data-testid="asset-visibility"><SelectValue /></SelectTrigger>
            <SelectContent>{VISIBILITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {visibility === "official"
              ? "Riservato: visibile solo ai Rivenditori Ufficiali (nascosto ai Potenziali)."
              : "Visibile a tutti i rivenditori, sia Potenziali che Ufficiali."}
          </p>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="asset-file"
            className="text-sm text-foreground file:mr-3 file:rounded-full file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-foreground" />
          <Button onClick={upload} disabled={uploading} data-testid="upload-asset-btn"
            className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
            {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {progress}%</> : <><UploadCloud className="mr-2 h-4 w-4" /> Salva materiale</>}
          </Button>
        </div>
        {uploading && file && (
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-brema-bg" data-testid="upload-progress">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">Solo testo (senza file) = va bene per caption/copy. Consigliati: Stories 1080×1920 (JPG/PNG/ZIP) · Foto ≥2000px · Video MP4 H.264 (&lt;100MB) · Lookbook PDF (&lt;25MB).</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {assets.map((a) => {
          const Icon = iconFor(a);
          const src = a.file_path ? fileUrl(a.file_path) : a.url;
          return (
            <div key={a._id} data-testid="admin-asset-card" className="flex flex-col overflow-hidden rounded-xl border border-border bg-brema-card">
              <div className="aspect-video overflow-hidden bg-brema-bg">
                {a.is_image && src ? <img src={src} alt={a.title} className="h-full w-full object-cover" />
                  : isVideoAsset(a) && src ? <video src={`${src}#t=0.1`} className="h-full w-full bg-black object-contain" preload="metadata" muted playsInline controls data-testid="admin-asset-video-preview" />
                  : a.thumb_path ? <img src={fileUrl(a.thumb_path)} alt={a.title} className="h-full w-full object-cover object-top" data-testid="admin-pdf-cover" />
                  : <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">{Icon && <Icon className="h-9 w-9" />}<span className="text-[11px] font-semibold uppercase">{a.type}</span></div>}
              </div>
              <div className="flex flex-1 flex-col p-4">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-brema-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand">{a.category}</span>
                  {a.platform && <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-foreground">{a.platform}</span>}
                  {a.visibility === "official"
                    ? <span data-testid="asset-visibility-badge" className="inline-flex items-center gap-1 rounded-full border border-brema-red/40 bg-brema-red/10 px-2 py-0.5 text-[10px] font-semibold text-red-500"><Lock className="h-2.5 w-2.5" /> Solo Ufficiali</span>
                    : <span data-testid="asset-visibility-badge" className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground"><Globe className="h-2.5 w-2.5" /> Tutti</span>}
                  <span className="text-xs text-muted-foreground">{a.size}</span>
                </div>
                <p className="mb-2 text-sm font-medium text-foreground">{a.title}</p>
                {a.body_text && <p className="mb-3 line-clamp-3 whitespace-pre-line rounded-md bg-brema-bg p-2 text-xs text-foreground">{a.body_text}</p>}
                <div className="mt-auto flex gap-2">
                  <Button size="sm" variant="secondary" data-testid="edit-asset-btn" onClick={() => openEditAsset(a)}
                    className="flex-1 rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Modifica
                  </Button>
                  <Button size="sm" variant="danger" data-testid="delete-asset-btn" onClick={() => del(a)}
                    className="rounded-full text-xs">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editAsset} onOpenChange={(o) => { if (!o) setEditAsset(null); }}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-border bg-brema-card" data-testid="edit-asset-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Modifica materiale</DialogTitle>
            <DialogDescription>Aggiorna i dati e la visibilità del materiale per i rivenditori.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Titolo</Label>
              <Input value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} data-testid="edit-asset-title" />
            </div>
            <div className="space-y-1.5"><Label>Categoria</Label>
              <Select value={editForm.category} onValueChange={(v) => setEditForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="edit-asset-category"><SelectValue /></SelectTrigger>
                <SelectContent>{ASSET_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editForm.category === "Social" && (
              <div className="space-y-1.5"><Label>Formato (Instagram / Facebook)</Label>
                <Select value={editForm.platform} onValueChange={(v) => setEditForm((f) => ({ ...f, platform: v }))}>
                  <SelectTrigger data-testid="edit-asset-platform"><SelectValue placeholder="Seleziona formato" /></SelectTrigger>
                  <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5"><Label>Testo / Caption</Label>
              <Textarea value={editForm.body_text} onChange={(e) => setEditForm((f) => ({ ...f, body_text: e.target.value }))} data-testid="edit-asset-text" rows={3} />
            </div>
            <div className="space-y-1.5"><Label>Visibilità Materiale</Label>
              <Select value={editForm.visibility} onValueChange={(v) => setEditForm((f) => ({ ...f, visibility: v }))}>
                <SelectTrigger data-testid="edit-asset-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>{VISIBILITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {editForm.visibility === "official"
                  ? "Riservato: visibile solo ai Rivenditori Ufficiali (nascosto ai Potenziali)."
                  : "Visibile a tutti i rivenditori, sia Potenziali che Ufficiali."}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEditAsset} disabled={savingEdit} data-testid="save-asset-edit-btn"
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CatalogAdmin() {
  return (
    <div>
      <PageHeader
        eyebrow="Plancia di comando Brand"
        title="Catalogo & Materiali"
        subtitle="Carica il catalogo prodotti (Excel + foto) e i materiali marketing scaricabili dai rivenditori."
      />
      <Tabs defaultValue="products">
        <TabsList className="flex-wrap bg-brema-card">
          <TabsTrigger value="products" data-testid="tab-admin-products"><Package className="mr-1.5 h-4 w-4" /> Prodotti</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-admin-materials"><Images className="mr-1.5 h-4 w-4" /> Materiali Marketing</TabsTrigger>
        </TabsList>
        <TabsContent value="products"><ProductsTab /></TabsContent>
        <TabsContent value="materials"><MaterialsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
